"use strict";

/**
 * @ngdoc function
 * @name diceApp.component:memberReport
 */
angular.module("diceApp").component("memberReport", {
  templateUrl: "es6/report/condensed/member/member.report.html",
  controllerAs: "reportController",
  controller: function (
    CondensedReportTemplate,
    CondensedReport,
    QuestionTemplate,
    MemberReport,
    Element,
    Pageable,
    Promises,
    language,
    descriptionFilter,
    numberFilter,
    stripHtmlFilter,
    $q
  ) {
    const reportController = this;

    reportController.$onInit = function () {
      reportController.pageable = Pageable.of({
        order: "displayName"
      });

      CondensedReportTemplate.query().$promise.then((templates) => {
        reportController.templates = _(templates)
          .filter({ personal: false })
          .map((template) => {
            return _.extend(template, { key: `${template.id}` });
          })
          .orderBy(["year", "id"], ["desc", "asc"])
          .value();

        reportController.parameters = {
          templateId: _(reportController.templates).map("id").head()
        };

        setReportColumns();
      });
    };

    reportController.onTemplate = function () {
      setReportColumns();
    };

    function setReportColumns() {
      let columns = [
        {
          code: "personName",
          name: "Person",
          visible: true
        },
        {
          code: "code",
          name: "Code",
          visible: true
        },
        {
          code: "name",
          name: "Name",
          visible: true
        },
        {
          code: "periodName",
          name: "Period",
          visible: true
        },
        {
          code: "amountOfParticipants",
          name: "Participants",
          visible: true
        },
        {
          code: "finishedPercentage",
          name: "Response rate",
          visible: true
        }
      ];

      getQuestionTemplates().then((questionTemplates) => {
        const questionHeaders = _.map(questionTemplates, ({ id, labels }) => ({
          id,
          code: `questions.${id}`,
          name: stripHtmlFilter(descriptionFilter(labels)),
          visible: true
        }));

        reportController.questionHeaders = questionHeaders;
        reportController.reportColumns = _.concat(columns, questionHeaders);
      });

      $q.all([
        Element.query({ type: "EVALUATION_TAG" }).$promise,
        Element.query({ type: "PERSON_TAG" }).$promise
      ]).then(([evaluationTags, personTags]) => {
        reportController.answerColumns = [
          {
            code: "person.externalId",
            name: "Person ID",
            visible: true
          },
          {
            code: "person.fullName",
            name: "Person",
            visible: true
          },
          {
            code: "evaluation.code",
            name: "Code",
            visible: true
          },
          {
            code: "evaluation.name",
            name: "Name",
            visible: true
          },
          {
            code: "evaluation.periodName",
            name: "Period",
            visible: true
          },
          ...evaluationTags.map(({ externalId, names, visible }) => ({
            code: `evaluation.tags.${externalId}`,
            name: descriptionFilter(names, externalId),
            visible
          })),
          {
            code: "questionNumber",
            name: "Question number",
            visible: true
          },
          {
            code: "questionLabel",
            name: "Question",
            visible: true
          },
          {
            code: "answer",
            name: "Answer",
            visible: true
          },
          ...personTags.map(({ externalId, names, visible }) => ({
            code: `tags.${externalId}`,
            name: descriptionFilter(names, externalId),
            visible
          }))
        ];
      });
    }

    reportController.search = function () {
      const pageable = reportController.pageable.build();

      reportController.loading = true;
      getPersons(pageable)
        .then((persons) => {
          reportController.persons = persons;
        })
        .finally(() => {
          reportController.loading = false;
        });
    };

    function getPersons(pageable) {
      const parameters = _.extend(
        {
          language: language.get()
        },
        reportController.parameters
      );

      return MemberReport.get(_.extend(parameters, pageable)).$promise;
    }

    reportController.expand = function (person) {
      person.expanded = person.expanded !== true;
      if (person.expanded && angular.isUndefined(person.loading)) {
        load(person);
      }
    };

    function load(person) {
      const parameters = _.extend(reportController.parameters, {
        language: language.get(),
        personId: person.id
      });

      person.loading = true;
      return MemberReport.evaluations(parameters)
        .$promise.then((evaluations) => {
          _.forEach(evaluations, setQuestions);
          person.evaluations = evaluations;
        })
        .finally(() => {
          person.loading = false;
        });
    }

    function setQuestions(evaluation) {
      evaluation.questions = {};
      _.forEach(evaluation.questionAnswers, (question) => {
        evaluation.questions[question.questionTemplateId] = CondensedReport.formatQuestionAnswers(question);
      });
    }

    reportController.getRows = function (page) {
      const pageable = reportController.pageable.build({ page, size: 50 });

      return getPersons(pageable).then((data) => {
        const persons = data.content;
        const requests = _.map(persons, (person) => () => load(person));
        return Promises.chain(requests).$promise.then(() => {
          const rows = mapToRows(persons);
          return _.extend(data, { content: rows });
        });
      });
    };

    function mapToRows(persons) {
      return _(persons)
        .map(({ displayName: personName, evaluations }) => {
          return _.map(
            evaluations,
            ({
              code,
              name,
              periodName,
              amountOfParticipants,
              finishedPercentage,
              questionAnswers
            }) => {
              let row = {
                personName,
                code,
                name,
                periodName,
                amountOfParticipants,
                finishedPercentage: numberFilter(finishedPercentage, 0),
                questions: {}
              };
              _.forEach(questionAnswers, (question) => {
                row.questions[question.questionTemplateId] = CondensedReport.formatQuestionAnswers(question);
              });
              return row;
            }
          );
        })
        .flatten()
        .value();
    }

    function getQuestionTemplates() {
      const template = _.find(reportController.templates, {
        id: reportController.parameters.templateId
      });

      const questionTemplates = _.get(template, "questionTemplates");
      if (_.isEmpty(questionTemplates)) {
        return $q.resolve([]);
      }

      return QuestionTemplate.bulk({
        id: _.map(questionTemplates, "questionTemplateId")
      }).$promise;
    }

    reportController.getAnswers = function (page) {
      const pageable = reportController.pageable.build({ page, size: 50 });

      return getPersons(pageable).then((data) => {
        const requests = _.map(
          data.content,
          (person) => () => getAnswers(person)
        );
        return Promises.chain(requests).$promise.then((answers) => {
          const rows = _(answers)
            .flatten()
            .sortBy([
              "person.externalId",
              "evaluation.code",
              "periodName",
              "questionLabel"
            ])
            .value();
          return _.extend(data, { content: rows });
        });
      });
    };

    function getAnswers(person) {
      const parameters = _.extend(reportController.parameters, {
        language: language.get(),
        personId: person.id
      });

      return MemberReport.answers(parameters).$promise;
    }
  }
});
